const PrivacyPolicy = () => {
    return (<>
    {/* LEGAL BANNER */}
    <div className="legal-banner">
        <h1>Privacy Policy for Digylax Pvt Ltd</h1>
        <p>At Digylax Pvt Ltd (“Digylax,” “we,” “us,” or “our”), we are committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard information when you use our enterprise software solutions, including HRMS, CRM, and MDM (collectively, the “Services”).</p>
        <p>By accessing or using the Services, you confirm that you have reviewed this Privacy Policy and understand how Digylax processes information in connection with the Services.</p>
    </div>
    {/* MAIN CONTENT */}
    <main className="legal-layout">
        <div className="legal-container">
            <header className="legal-header">
                <h2>Our Commitment to Privacy</h2>
                <p className="last-updated">Effective Date: June 20, 2025 | Last Updated: January 05, 2026</p>
            </header>

            <section className="legal-content">
                
                <h3>I. Information We Collect</h3>
                <p>We collect information primarily from your organization and through your use of the Services.</p>

                <h4>1. Personal and Account Data</h4>
                <ul>
                    <li><strong>Identity and Contact Data:</strong> Name, business email address, phone number, job role, and login credentials (stored in hashed form).</li>
                    <li><strong>Billing and Payment Data:</strong> Billing address and payment-related information. Payment processing is handled through secure, PCI-compliant third-party gateways (such as Razorpay, PayPal, or Stripe). Digylax does not store full credit or debit card numbers.</li>
                    <li><strong>User Activity Data:</strong> Information used for authentication, system security, audit logs, and customer support.</li>
                </ul>

                <h4>2. Business Content Data (Data Processed on Your Behalf)</h4>
                <ul>
                    <li><strong>Customer Content:</strong> Data uploaded, stored, or generated by your organization’s users while using the Services. This may include employee records, HR-sensitive information, documents, images, customer contact data, device configurations, or other proprietary business information.</li>
                    <li><strong>Role as Data Processor:</strong> For Business Content Data, Digylax acts as a Data Processor and processes such data strictly in accordance with your organization’s instructions. Your organization remains the Data Controller. Where required by law (e.g., GDPR), this relationship is governed by a separate Data Processing Addendum (DPA).</li>
                    <li>This includes <strong>employee attendance records, punch-in/punch-out logs, associated photographs (if enabled), work schedules, leave data, and organizational records</strong> generated while using the Services.</li>
                    <li>Digylax acts as a <strong>Data Processor</strong>, processing such data strictly based on the organization’s instructions. The organization remains the <strong>Data Controller</strong>.</li>
                </ul>

                <h4>3. Technical and Usage Data</h4>
                <ul>
                    <li><strong>Technical Identifiers:</strong> IP address, browser type, device identifiers, operating system, and application version.</li>
                    <li><strong>Usage and Analytics Data:</strong> Logs related to features accessed, time spent, pages viewed, and error reporting, collected to monitor performance and improve the Services.</li>
                </ul>

                <h3>II. Camera Access and Image Capturing</h3>
                <p>
                    Our application may request access to the device's camera solely to support photo-based
                    punch-in and punch-out functionality, as well as other user-initiated features.
                </p>
                <ul>
                    <li>Camera access is enabled <strong>only when the user explicitly initiates a punch-in or punch-out action.</strong></li>
                    <li>The application <strong>does not access the camera in the background </strong>or without user knowledge.</li>
                    <li>Photographs captured during attendance marking are used only for<strong> identity verification and attendance validation purposes.</strong></li>
                    <li>Captured images are treated as <strong>Business Content Data</strong>and are accessible only to authorized organizational administrators.</li>
                    <li>Digylax does <strong>not sell, share, or use captured images </strong>for advertising, profiling, or tracking purposes.</li>
                </ul>

                <h3>III. Location Access and Usage</h3>
                <ul>
                    <li>Our application collects <strong>precise (GPS-level) location data</strong> to support attendance tracking and workplace verification features.</li>
                    <li>This location data may be collected even when the application is closed or not in active use during scheduled work hours, to ensure accurate check-in/check-out reporting and to prevent attendance misuse.</li>
                    <li>Approximate location data (such as city or state) may also be derived from an IP address for purposes including security monitoring and fraud prevention.</li>
                    <li>Users must explicitly grant <strong>"Allow all the time"</strong> location permission for background tracking to function. If permission is not granted, attendance-related features will be disabled.</li>
                    <li>Users may choose not to grant location access; however, certain features—especially attendance tracking—will not be available as a result.</li>
                    <li>Digylax does not sell, rent, or share location data for advertising or marketing purposes.</li>
                </ul>
                
                <h3>IV. Why We Collect and Use Your Information (Purpose of Processing)</h3>
                <p>We collect and use Personal Data and Business Content Data for the following purposes:</p>

                <h4>4. Processing Necessary for Contract</h4>
                <ul>
                    <li>To <strong>provide, operate, maintain, and secure the Services</strong>, including employee attendance, leave management, and workforce operations.</li>
                    <li>To <strong>record employee attendance and working hours</strong>, including validation through <strong>location-enabled and photo-based punch-in and punch-out</strong>, where such features are enabled by the organization.</li>
                    <li>To <strong>manage shift schedules, leave requests, payroll-related inputs, and daily workforce activities</strong>.</li>
                    <li>To manage subscriptions, billing, service communications, and system notifications.</li>
                    <li>To provide technical support and respond to inquiries from users or administrators.</li>
                </ul>

                <h4>5. Legitimate Business Interests</h4>
                <ul>
                    <li><strong>Service Improvement:</strong> To analyze usage data and improve functionality, performance, and user experience.</li>
                    <li><strong>Security and Integrity:</strong> To ensure security, accuracy, and compliance in HR processes, and to detect, prevent, and respond to fraud, misuse, or security threats.</li>
                    <li><strong>Access Control:</strong> To provide <strong>secure, role-based access</strong> for employees, managers, and HR teams.</li>
                    <li><strong>Communications:</strong> To send service-related updates and product information. Marketing communications, where applicable, include opt-out options.</li>
                </ul>

                <h4>6. Compliance with Law</h4>
                <ul>
                    <li>To comply with applicable legal obligations, regulatory requirements, audits, or lawful requests from government or regulatory authorities.</li>
                </ul>

                <h3>V. Data Sharing, Storage, and Security</h3>

                <h4>7. Data Sharing and Disclosure</h4>
                <ul>
                    <li><strong>No Sale of Data:</strong> Digylax does not sell or rent Personal Data or Business Content Data.</li>
                    <li><strong>Service Providers:</strong> Data may be shared with trusted third-party service providers (such as cloud hosting providers, payment processors, and analytics services) under strict confidentiality and security obligations.</li>
                    <li><strong>Legal Requirements:</strong> Data may be disclosed if required by law, regulation, or valid legal process.</li>
                </ul>

                <h4>8. Data Storage and International Transfers</h4>
                <ul>
                    <li>Data is primarily stored on secure cloud servers located in India.</li>
                    <li>For redundancy, performance, or business continuity, data may be processed in other jurisdictions (such as the United States or Singapore), subject to appropriate legal safeguards, including Standard Contractual Clauses where applicable.</li>
                </ul>

                <h4>9. Security Measures</h4>
                <ul>
                    <li>We implement industry-standard security controls, including encryption in transit and at rest, access controls, firewalls, and continuous monitoring.</li>
                    <li>While we take strong measures to protect data, no system can guarantee absolute security.</li>
                </ul>

                <h3>VI. Cookies, Tracking, and User Rights</h3>

                <h4>10. Cookies and Tracking Technologies</h4>
                <p>
                    We use first-party and third-party cookies and similar technologies for session management,
                    performance optimization, and analytics. By using the Services, you consent to such use as described.
                </p>

                <h4>11. Your Data Rights</h4>
                <p>
                    Depending on your jurisdiction, you may have rights to access, correct, delete, restrict, 
                    or object to the processing of your Personal Data. Requests can be submitted 
                    to <strong><a href="mailto:support@digylax.com" className="link-dark">support@digylax.com</a></strong>, and 
                    we will respond within legally required timelines.
                </p>

                <h4>12. Data Retention</h4>
                <p>
                    We retain Personal Data and Business Content Data only for as long as necessary to provide the
                    Services, comply with legal obligations, resolve disputes, or enforce agreements. Data is securely
                    deleted or anonymized thereafter.
                </p>

                <h4>13. Children’s Privacy</h4>
                <p>
                    The Services are intended for business users aged 18 and above. We do not knowingly collect
                    personal information from individuals under 18.
                </p>

                <h3>VII. Policy Updates and Contact</h3>

                <h4>14. Policy Updates</h4>
                <p>
                    We may update this Privacy Policy from time to time. The “Last Updated” date will be revised
                    accordingly, and material changes will be communicated through the Services or by email.
                </p>

                <h4>15. Background Location Tracking and Workplace Verification</h4>
                <p>
                    Precise (GPS-level) location data is collected to support attendance tracking and workplace verification.
                    This data is collected even when the app is closed or not in use during scheduled work hours to ensure
                    accurate check-in/check-out reporting. This data is used solely for HR management purposes and is not
                    shared with third parties for marketing.
                </p>

                <h4>16. Contact Information</h4>
                <p>If you have any questions or concerns regarding this Privacy Policy or our data practices, please contact:</p>
                <div className="contact-info mt-2">
                    <p>
                        <strong>Digylax Pvt Ltd</strong> <br />
                        Attention: Legal / Privacy Office <br />
                        N8, Souvenir, Kalapatti Road,<br />
                        Coimbatore - 641014, Tamil Nadu, India<br />
                        Email: <strong><a href="mailto:support@digylax.com" className="link-dark">support@digylax.com</a></strong>
                    </p>
                </div>
            </section>
        </div>
    </main>
    </>);
}
 
export default PrivacyPolicy;